import React from "react";
import { Localization } from "@viamap/viamap2-common";
import { ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { LayerType } from "src/common/managers/Types";
import { AnnotationType, AnnotationContextType, type ANNAnnotationRecord } from "./ANNTypes";
import { BsEye, BsDownload, BsArrowUpRightSquareFill, BsTrash, BsPencil, BsFileText, BsClockHistory } from "react-icons/bs";
interface AnnotationActionsCellProps {
   record?: ANNAnnotationRecord;
   importingId: string | null;
   downloadingId: string | null;
   deletingId: string | null;
   editingId: string | null;
   canVerify: boolean;
   canDelete?: boolean;
   canWrite?: boolean;
   contextType?: AnnotationContextType;
   onShowNote: (record: ANNAnnotationRecord) => void;
   onImport: (record: ANNAnnotationRecord) => void;
   onDownloadDocument: (record: ANNAnnotationRecord) => void;
   onDelete: (record: ANNAnnotationRecord) => void;
   onEditNote?: (record: ANNAnnotationRecord) => void;
   onEditStedfaestelse?: (record: ANNAnnotationRecord) => void;
   onEditDocument?: (record: ANNAnnotationRecord) => void;
   onOpenServitutDetails?: (record: ANNAnnotationRecord) => void;
   showServitutDetailsButton?: boolean;
   alwaysShowDownload?: boolean;
   onToggleHistory?: (record: ANNAnnotationRecord) => void;
   isHistoryOpen?: boolean;
   historyAvailable?: boolean;
}
export function ANNAnnotationActionsCell(props: AnnotationActionsCellProps) {
   const { record, importingId, downloadingId, deletingId, editingId, canVerify, canDelete = true, canWrite = false, contextType, onShowNote, onImport, onDownloadDocument, onDelete, onEditNote, onEditStedfaestelse, onEditDocument, onOpenServitutDetails, showServitutDetailsButton, alwaysShowDownload, onToggleHistory, isHistoryOpen, historyAvailable = true } = props;
   if (!record) {
      return null;
   }
   const isDeleting = deletingId === record.id;
   const editButtonTitle = canVerify
      ? Localization.getText("Edit & Verify")
      : Localization.getText("Edit annotation");
   if (record.type === AnnotationType.Note) {
      return (
         <div className="ann-actions-cell">
            {onToggleHistory && (
               <ViewButtonIcon
                  disabled={!historyAvailable}
                  onClick={() => historyAvailable && onToggleHistory(record)}
                  title={
                     !historyAvailable
                        ? Localization.getText("No history")
                        : isHistoryOpen
                           ? Localization.getText("Hide history")
                           : Localization.getText("Show history")
                  }
               >
                  <BsClockHistory size={16} />
               </ViewButtonIcon>
            )}
            {showServitutDetailsButton && record.documentId && onOpenServitutDetails && (
               <ViewButtonIcon
                  onClick={() => onOpenServitutDetails(record)}
                  title={Localization.getText("Open servitut details")}
               >
                  <BsFileText size={16} />
               </ViewButtonIcon>
            )}
            <ViewButtonIcon
               onClick={() => onShowNote(record)}
               title={Localization.getText("Show_vis")}
            >
               <BsEye size={16} />
            </ViewButtonIcon>
            {canWrite && onEditNote && (
               <ViewButtonIcon
                  disabled={editingId === record.id}
                  onClick={() => onEditNote(record)}
                  title={editingId === record.id ? Localization.getText("Editing...") : editButtonTitle}
               >
                  <BsPencil size={16} />
               </ViewButtonIcon>
            )}
            {canDelete && (
               <ViewButtonIcon
                  disabled={isDeleting}
                  onClick={() => onDelete(record)}
                  title={Localization.getText("Delete")}
               >
                  <BsTrash size={16} />
               </ViewButtonIcon>
            )}
         </div>
      );
   }
   if (record.type === AnnotationType.Document) {
      const showDownload = alwaysShowDownload !== false;
      return (
         <div className="ann-actions-cell">
            {onToggleHistory && (
               <ViewButtonIcon
                  disabled={!historyAvailable}
                  onClick={() => historyAvailable && onToggleHistory(record)}
                  title={
                     !historyAvailable
                        ? Localization.getText("No history")
                        : isHistoryOpen
                           ? Localization.getText("Hide history")
                           : Localization.getText("Show history")
                  }
               >
                  <BsClockHistory size={16} />
               </ViewButtonIcon>
            )}
            {showServitutDetailsButton && record.documentId && onOpenServitutDetails && (
               <ViewButtonIcon
                  onClick={() => onOpenServitutDetails(record)}
                  title={Localization.getText("Open servitut details")}
               >
                  <BsFileText size={16} />
               </ViewButtonIcon>
            )}
            {showDownload && (
               <ViewButtonIcon
                  disabled={!record.document?.s3Key || downloadingId === record.id}
                  onClick={() => onDownloadDocument(record)}
                  title={downloadingId === record.id ? Localization.getText("Downloading...") : Localization.getText("Download")}
               >
                  <BsDownload size={16} />
               </ViewButtonIcon>
            )}
            {canWrite && onEditDocument && (
               <ViewButtonIcon
                  disabled={editingId === record.id}
                  onClick={() => onEditDocument(record)}
                  title={editingId === record.id ? Localization.getText("Editing...") : editButtonTitle}
               >
                  <BsPencil size={16} />
               </ViewButtonIcon>
            )}
            {canDelete && (
               <ViewButtonIcon
                  disabled={isDeleting}
                  onClick={() => onDelete(record)}
                  title={Localization.getText("Delete")}
               >
                  <BsTrash size={16} />
               </ViewButtonIcon>
            )}
         </div>
      );
   }
   const layers = record.layers || [];
   const hasData = layers.some(
      (layer) => {
         if (layer.type === LayerType.ImageOverlay && layer.geoJson?.features?.length) {
            return true;
         }
         if (layer.type === LayerType.ImageOverlay && layer.imageOverlay?.coordinates?.length) {
            return true;
         }
         if ((layer.type === LayerType.GeoJSON_Line || layer.type === LayerType.GeoJSON_Polygon || layer.type === LayerType.GeoJSON_Point) &&
            layer.geoJson?.features?.length) {
            return true;
         }
         return false;
      }
   );
   const disabled = importingId === record.id || !hasData;
   return (
      <div className="ann-actions-cell">
         {onToggleHistory && (
            <ViewButtonIcon
               disabled={!historyAvailable}
               onClick={() => historyAvailable && onToggleHistory(record)}
               title={
                  !historyAvailable
                     ? (Localization.getText("No history"))
                     : isHistoryOpen
                        ? (Localization.getText("Hide history"))
                        : (Localization.getText("Show history"))
               }
            >
               <BsClockHistory size={16} />
            </ViewButtonIcon>
         )}
         {showServitutDetailsButton && record.documentId && onOpenServitutDetails && (
            <ViewButtonIcon
               onClick={() => onOpenServitutDetails(record)}
               title={Localization.getText("Open servitut details")}
            >
               <BsFileText size={16} />
            </ViewButtonIcon>
         )}
         <ViewButtonIcon
            disabled={disabled}
            onClick={() => onImport(record)}
            title={importingId === record.id ? Localization.getText("Loading") : "tilføj som kortlag"}
         >
            <BsArrowUpRightSquareFill size={16} />
         </ViewButtonIcon>
         {canWrite && onEditStedfaestelse && (
            <ViewButtonIcon
               disabled={editingId === record.id}
               onClick={() => onEditStedfaestelse(record)}
               title={editingId === record.id ? Localization.getText("Editing...") : editButtonTitle}
            >
               <BsPencil size={16} />
            </ViewButtonIcon>
         )}
         {canDelete && (
            <ViewButtonIcon
               disabled={isDeleting}
               onClick={() => onDelete(record)}
               title={Localization.getText("Delete")}
            >
               <BsTrash size={16} />
            </ViewButtonIcon>
         )}
      </div>
   );
}
